import { useState } from "react"
import { Box, Collapse, Link, Stack, Typography } from "@mui/material"
import { Repeat } from "lib/@components/repeat"
import { DemandChip } from "./demand-chip"
import { SOURCE_LABELS, ORIGIN_LABELS } from "./group-by-source"

/**
 * One grant source and the demands it is responsible for.
 *
 * Type and origin are plain text, not chips. Chips are reserved for the demands themselves,
 * where colour carries real meaning (forced / added / removed) — putting them round metadata
 * too made everything look equally significant.
 *
 * Only the demands this source is the ATTRIBUTED grantor of; see group-by-source.js. A product
 * whose demands are wholly covered by a broader one the client also holds never reaches this
 * component. The list is collapsed by default: a Tier 3 product alone contributes ~20 demands,
 * which buries the structure the section exists to show.
 */
export function GrantedSourceGroup({ item: group }) {
    const [open, setOpen] = useState(false)
    const { demands } = group

    return (
        <Box data-cy={`access-source-${group.sourceId}`}>
            <Stack direction="row" spacing={1} alignItems="baseline" useFlexGap flexWrap="wrap">
                <Typography variant="caption" color="text.secondary">
                    {SOURCE_LABELS[group.source] ?? group.source}
                </Typography>
                <Typography variant="subtitle2">{label()}</Typography>
                <Link
                    component="button"
                    variant="caption"
                    underline="hover"
                    onClick={toggle}
                    data-cy="access-source-toggle"
                >
                    {open ? "hide" : `show ${demands.length} demand${demands.length === 1 ? "" : "s"}`}
                </Link>
            </Stack>
            <Collapse in={open} unmountOnExit>
                <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" pl={1} pt={0.5}>
                    <Repeat list={demands} item={<DemandChip />} />
                </Stack>
            </Collapse>
        </Box>
    )

    function label() {
        // The origin only means anything for products, where the same tier can arrive from more
        // than one place and be summed.
        if (!group.origin) return group.sourceLabel
        return `${group.sourceLabel} (${ORIGIN_LABELS[group.origin] ?? group.origin})`
    }

    function toggle() {
        setOpen((current) => !current)
    }
}
